import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FC, ReactNode, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  DiskListIcon,
  HyperlinkIcon,
  ItalicIcon,
  NumberListIcon,
  UnderlineIcon,
} from '../../assets/icons/index';
import { getPlainTextFromHtml } from '../../utils/get-plain-text-from-html.util';

type Props = {
  disabled?: boolean;
  label?: string;
  maxLength?: number;
  onChange?: (html: string) => void;
  placeholder?: string;
  value?: string;
};
const ActionButton = ({
  label,
  onClick,
  isActive,
}: {
  isActive?: boolean;
  label: ReactNode;
  onClick: () => void;
}) => (
  <button
    className={twMerge(
      'rounded-md px-2 py-1 text-sm',
      'hover:bg-gray-100',
      isActive ? 'bg-gray-100 font-semibold' : 'text-gray-600',
    )}
    onClick={onClick}
    type="button"
  >
    {label}
  </button>
);

export const TextEditor: FC<Props> = ({
  value = '',
  onChange,
  placeholder,
  disabled,
  maxLength,
  label,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['paragraph', 'listItem'],
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          className: 'text-brand-600 underline hover:text-brand-700',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  const handleSetLink = () => {
    const prevUrl = (editor.getAttributes('link').href as string | undefined) ?? '';
    const url = window.prompt('Enter URL', prevUrl || 'https://');

    if (url === null) return;

    if (url === '') return editor.chain().focus().extendMarkRange('link').unsetLink().run();

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleUnsetLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  const plainValue = getPlainTextFromHtml(value);

  return (
    <div className="flex flex-col gap-3">
      {label && <span className="pl-4 text-xs font-[450] text-gray-500">{label}</span>}
      <div
        className={twMerge(
          'flex flex-col gap-2 rounded-2xl border border-gray-200 bg-gray-50 p-4 pb-2',
          disabled && 'rounded-none border-none p-0',
        )}
      >
        {!disabled && (
          <div className="mb-3 flex flex-wrap items-center gap-1">
            <ActionButton
              isActive={editor.isActive('bold')}
              label={<BoldIcon />}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ActionButton
              isActive={editor.isActive('italic')}
              label={<ItalicIcon />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />

            <ActionButton
              isActive={editor.isActive('underline')}
              label={<UnderlineIcon />}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <ActionButton
              isActive={editor.isActive('link')}
              label={<HyperlinkIcon />}
              onClick={() => (editor.isActive('link') ? handleUnsetLink() : handleSetLink())}
            />

            <span className="mx-1 h-4 w-px bg-gray-200" />

            <ActionButton
              isActive={editor.isActive('bulletList')}
              label={<DiskListIcon />}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />

            <ActionButton
              isActive={editor.isActive('orderedList')}
              label={<NumberListIcon />}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />

            <span className="mx-1 h-4 w-px bg-gray-200" />

            <ActionButton
              isActive={editor.isActive({ textAlign: 'left' })}
              label={<AlignLeftIcon />}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            />
            <ActionButton
              isActive={editor.isActive({ textAlign: 'center' })}
              label={<AlignCenterIcon />}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
            />
            <ActionButton
              isActive={editor.isActive({ textAlign: 'right' })}
              label={<AlignRightIcon />}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            />
          </div>
        )}
        <div className={twMerge('rounded-xl bg-white px-3 py-2', disabled && 'rounded-none p-0')}>
          <EditorContent
            className={twMerge(
              'prose max-h-[600px] min-h-[120px] max-w-none outline-none',
              disabled && 'min-h-fit',
            )}
            disabled={disabled}
            editor={editor}
          />
        </div>
        {maxLength && (
          <p
            className={twMerge(
              'text-end text-xs font-medium text-gray-400',
              plainValue.length > maxLength && 'text-fireside-600',
            )}
          >
            {plainValue.length}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
};
